import { createQuery } from '@tanstack/solid-query'
import { eventStartDate } from '~/config'
import type { ItemRow } from '~/lib/reward-types'
import { api, getCurrentUserId } from '~/services/api'

export type ClaimedItemsPage = {
  items: ItemRow[]
  next_id?: number | null
}

export type FetchClaimedItemsPage = (params: {
  limit: number
  startId?: number
}) => Promise<ClaimedItemsPage>

const PAGE_LIMIT = 50

export const itemsQueryKey = ['items'] as const

/**
 * Cursor-paginates through claimed items. Stops when the server runs out of
 * pages OR when a returned item is older than `eventStartMs` — anything that
 * predates the campaign belongs to a previous event and is not part of this
 * campaign's history.
 */
export async function loadClaimedItems(deps: {
  fetchPage: FetchClaimedItemsPage
  eventStartMs: number
  limit?: number
}): Promise<ItemRow[]> {
  const limit = deps.limit ?? PAGE_LIMIT
  const allItems: ItemRow[] = []
  let startId: number | undefined

  while (true) {
    const response = await deps.fetchPage({ limit, startId })
    allItems.push(...response.items)
    const reachedPreEventHistory = response.items.some(
      (item) => new Date(item.created_at).getTime() < deps.eventStartMs,
    )
    if (reachedPreEventHistory) break
    if (response.next_id == null) break
    startId = response.next_id
  }
  return allItems
}

export const useItems = () => {
  return createQuery(() => ({
    queryKey: itemsQueryKey,
    queryFn: async () => {
      const userId = await getCurrentUserId()
      return loadClaimedItems({
        eventStartMs: eventStartDate.getTime(),
        fetchPage: async ({ limit, startId }) => {
          const searchParams: Record<string, number> = { limit }
          if (startId !== undefined) searchParams.start_id = startId
          return api.service
            .get(`delivery/users/${userId}/items/claimed`, { searchParams })
            .json<ClaimedItemsPage>()
        },
      })
    },
    staleTime: Infinity,
  }))
}
